<script lang="ts" setup>
import { PropType, ref, onMounted, computed, watch } from "vue";
import { Modal, Dropdown } from "@/components";
import { type Config } from "./Config";
import { useI18n } from "vue-i18n";
import {
  useMyAdminTeamsShortQuery,
  useCopyDocumentSpaceConfigMutation,
  useDocumentSpaceCompatibilityGroupsQuery,
} from "@/graphql/operations";
import { useFlashMessage } from "@/hooks/useFlashMessage";
import { useRouter } from "vue-router";
import { useForm, useField } from "vee-validate";
import { string } from "yup";

const { t } = useI18n();
const router = useRouter();
const { displayFlashMessage } = useFlashMessage();

const props = defineProps({
  config: {
    type: Object as PropType<Config>,
    required: true,
  },
});

const emits = defineEmits(["close"]);

const { data } = useMyAdminTeamsShortQuery();
const teamNodes = computed(() => (data.value?.myAdminTeams?.nodes ?? []).filter(Boolean));
const teams = computed(() =>
  data.value?.myAdminTeams?.nodes?.map((t) => ({
    value: t!.id.toString(),
    title: t!.name,
  }))
);

const tenantIdByTeamId = computed(() => {
  const map = new Map<string, string>();
  for (const team of teamNodes.value) {
    if (team?.tenant?.id) {
      map.set(team.id.toString(), team.tenant.id.toString());
    }
  }
  return map;
});

const { validate, errors } = useForm();

const { value: name } = useField<string>("name", string().required(), {
  initialValue: t("templateEditor.copyModal.copyName", {
    name: props.config.name,
  }),
});

const input_name = ref<HTMLInputElement>();
onMounted(() => {
  if (!input_name.value) {
    return;
  }

  input_name.value!.select();
});

const teamId = ref(props.config.teamId.toString());
const setTeamId = (id: string) => {
  teamId.value = id;
};

const selectedTenantId = computed(() => tenantIdByTeamId.value.get(teamId.value) || null);

const { data: compatibilityGroupsData } = useDocumentSpaceCompatibilityGroupsQuery();
const allCompatibilityGroups = computed(() =>
  compatibilityGroupsData.value?.documentSpaceLinkGroups?.nodes
    ?.filter(Boolean)
    .map((g) => ({
      value: g!.id.toString(),
      title: g!.name,
      tenantId: g!.tenantId?.toString(),
    })) || []
);

const compatibilityGroups = computed(() => {
  const tenantId = selectedTenantId.value;
  if (!tenantId) {
    return [];
  }

  return (
    allCompatibilityGroups.value
      .filter((g) => g.tenantId === tenantId)
      .map((g) => ({ value: g.value, title: g.title })) || []
  );
});

const defaultCompatibilityGroupId = computed(() => {
  const groups = compatibilityGroups.value;
  if (groups.length === 0) {
    return null;
  }

  // The group label is user-editable; treat the lowest id group per tenant as the default.
  return groups.reduce((minId, g) => (BigInt(g.value) < BigInt(minId) ? g.value : minId), groups[0].value);
});

const compatibilityGroupId = ref(props.config.compatibilityGroupId || "");

const ensureGroupSelectionValid = () => {
  const groups = compatibilityGroups.value;

  if (groups.length === 0) {
    // If we can't see groups for the selected tenant (RLS), let the backend pick the default.
    compatibilityGroupId.value = "";
    return;
  }

  if (compatibilityGroupId.value && groups.some((g) => g.value === compatibilityGroupId.value)) {
    return;
  }

  compatibilityGroupId.value = defaultCompatibilityGroupId.value || groups[0].value;
};

watch([selectedTenantId, compatibilityGroups], ensureGroupSelectionValid, { immediate: true });

const setCompatibilityGroupId = (id: string) => {
  compatibilityGroupId.value = id;
};

const { executeMutation } = useCopyDocumentSpaceConfigMutation();
const submit = async () => {
  const validationResult = await validate();
  if (!validationResult.valid) {
    console.log("invalid data");
    return;
  }

  const res = await executeMutation({
    id: props.config.id,
    teamId: teamId.value,
    name: name.value,
    copyTranslations: true,
    compatibilityGroupId: compatibilityGroupId.value || null,
  });

  const newConfigId = res.data?.copyDocumentSpaceConfig?.newConfig?.id;
  if (newConfigId) {
    const url = router.resolve({
      name: "TemplateEditor",
      params: { id: newConfigId },
    }).href;
    window.open(url, "_blank");
    return;
  }

  displayFlashMessage(t("error"), "error");
};
</script>

<template>
  <Modal :active="true" @close="emits('close')" :scrolling="true">
    <template #header>
      {{ $t("templateEditor.copyModal.title") }}
    </template>

    <template #body>
      <div class="row">
        <input
          type="text"
          class="input"
          v-model="name"
          ref="input_name"
          :class="{ invalid: errors.name !== undefined }"
        />
      </div>

      <div class="row" v-if="teams && teams.length > 1">
        <label>{{ $t("templateEditor.copyModal.teamSelection.label") }}</label>
        <dropdown
          :items="teams"
          :value="teamId"
          :displayTitle="true"
          @change="setTeamId"
        />
      </div>

      <div
        class="row"
        v-if="compatibilityGroups && compatibilityGroups.length > 1"
      >
        <label>{{
          $t("templateEditor.copyModal.compatibilityGroupSelection.label")
        }}</label>
        <dropdown
          :items="compatibilityGroups"
          :value="compatibilityGroupId"
          :displayTitle="true"
          @change="setCompatibilityGroupId"
        />
      </div>
    </template>

    <template #footer>
      <div class="buttons">
        <button
          class="button primary"
          @click="submit"
          :class="{ disabled: errors.name !== undefined }"
        >
          {{ $t("templateEditor.copyModal.submit") }}
        </button>
        <button class="button" @click="emits('close')">
          {{ $t("cancel") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
.row {
  display: flex;
  align-items: center;
  margin-bottom: 1.5em;
  font-size: 0.9em;
}

label {
  margin-right: 0.5em;
}
</style>
